// src/pages/StaticFeesManager.jsx
import React, { useEffect, useState, useMemo } from "react";
import {
  getStaticFeeSummary,
  listStaticFees,
  createStaticFee,
  updateStaticFee,
  deleteStaticFee,
} from "../services/api";



// Academic years 2014-2015 ... 2024-2025 (11 years)
const generateAcademicYears = (start = 2014, count = 11) =>
  Array.from({ length: count }).map((_, i) => `${start + i}-${start + i + 1}`);

const emptyStudent = {
  fullName: "",
  totalTuition: 0,
  paidAmount: 0,
  remainder: 0,
  phone: "",
  note: ""
};

const StaticFeesManager = () => {
  const academicYears = generateAcademicYears(); // defaults 2014..2024

  const [view, setView] = useState("dashboard"); // dashboard | details
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(false);

  // filters for cards
  const [filterYear, setFilterYear] = useState(""); // "" means all
  const [filterClass, setFilterClass] = useState("ALL"); // "ALL" means all classes

  // details view state
  const [selectedGroup, setSelectedGroup] = useState(null); // { academicYear, className }
  const [students, setStudents] = useState([]);
  const [newRows, setNewRows] = useState([]);
  const [search, setSearch] = useState("");

  // modal for creating a new group (choose year+class)
  const [showAddModal, setShowAddModal] = useState(false);

  useEffect(() => {
    // initial load of dashboard
    fetchDashboard({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // whenever filters change, re-fetch automatically (no Apply button)
  useEffect(() => {
    const params = {};
    if (filterYear) params.academicYear = filterYear;
 
    fetchDashboard(params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterYear]);

  const fetchDashboard = async (params = {}) => {
    setLoading(true);
    try {
      // call backend summary endpoint with optional params
      const res = await getStaticFeeSummary(params);
      setCards(res.data || []);
    } catch (err) {
      console.error("fetchDashboard error:", err);
      setCards([]);
    } finally {
      setLoading(false);
    }
  };

  const openClassDetails = async (group) => {
    setLoading(true);
    setSelectedGroup(group);
    try {
      const res = await listStaticFees({
        academicYear: group.academicYear,
        limit: 2000
      });
      setStudents(res.data || []);
      setNewRows([]);
      setView("details");
    } catch (err) {
      console.error("openClassDetails error", err);
      setStudents([]);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToDashboard = () => {
    setView("dashboard");
    setSelectedGroup(null);
    setStudents([]);
    // fetch with current filters (use same logic as effect)
    const params = {};
    if (filterYear) params.academicYear = filterYear;
  
    fetchDashboard(params);
  };


  return (
    <div dir="rtl" className="min-h-screen bg-gray-50 p-6 font-sans text-gray-800">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-extrabold text-indigo-900">
              {view === "dashboard" ? "الذمم المالية — للطلاب غير المسجلين" : ` ${selectedGroup?.academicYear}`}
            </h1>
            <p className="text-slate-700 text-lg mt-1">
              {view === "dashboard"
                ? "لوحة تلخيصية للأعوام الدراسية  ."
                : "تحرير ورقة العام الدراسي"
}
            </p>
          </div>

          <div className="flex items-center gap-3">
            {view === "details" && (
              <button onClick={handleBackToDashboard} className="bg-white text-slate-700 font-semibold border px-4 py-2 rounded-lg shadow-md  hover:bg-gray-50">
                الرجوع للأرشيف
              </button>
            )}

            {view === "dashboard" && (
              <button onClick={() => setShowAddModal(true)} className="bg-indigo-600 text-white px-5 py-3 rounded-lg shadow">
                إنشاء ورقة عام دراسي 
              </button>
            )}
          </div>
        </div>

        {/* Filters (only on dashboard) */}
        {view === "dashboard" && (
          <div className="bg-white p-4 rounded-xl shadow-sm mb-6 flex flex-col md:flex-row gap-3 items-end">
            <div className="flex items-center gap-3">
              <div>
                <label className="block text-md text-slate-900 mb-1">السنة الدراسية</label>
                <select
                  value={filterYear}
                  onChange={(e) => setFilterYear(String(e.target.value || "").trim())}
                  className="border rounded px-3 py-2 min-w-[180px]"
                >
                  <option value="">كل السنوات</option>
                  {academicYears.map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>


            </div>

            <div className="md:ml-auto text-md text-slate-900 mt-4 md:mt-0">
              {filterYear ? (
                <div>مرشح بحسب: {filterYear ? <span className="font-medium mr-2">{filterYear}</span> : null}</div>
              ) : (
                <div>عرض كل البطاقات</div>
              )}
            </div>
          </div>
        )}

        {view === "dashboard" ? (
          <DashboardView loading={loading} cards={cards} onOpen={openClassDetails} />
        ) : (
          <DetailsView
            group={selectedGroup}
            students={students}
            setStudents={setStudents}
            newRows={newRows}
            setNewRows={setNewRows}
            search={search}
            setSearch={setSearch}
          />
        )}
      </div>

      {showAddModal && (
        <AddClassModal
          years={academicYears}
          onClose={() => setShowAddModal(false)}
          onSuccess={(g) => {
            setShowAddModal(false);
            // open details immediately for the created group
            openClassDetails(g);
          }}
        />
      )}
    </div>
  );
};

/* ------------------
   Dashboard View
   ------------------ */
const DashboardView = ({ loading, cards, onOpen }) => {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {loading ? (
          <div className="col-span-full text-center py-10">جاري تحميل البطاقات...</div>
        ) : cards.length === 0 ? (
          <div className="col-span-full bg-white rounded-2xl border border-dashed border-gray-300 py-20 text-center text-gray-400">
            لا توجد سجلات — أنشئ ورقة عام دراسي.
          </div>
        ) : (
          cards.map((c, idx) => (
            <div
              key={`${c.academicYear}-${idx}`}
              onClick={() => onOpen({ academicYear: c.academicYear})}
              className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100 hover:shadow-lg cursor-pointer transition"
            >
              <div className="flex justify-between items-start">
                <div className="flex items-center gap-4">
                  <div className="bg-indigo-50 text-indigo-600 rounded-lg p-3 text-xl">📁</div>
                  <div>
                    <div className="text-lg text-slate-900">{c.academicYear}</div>
                    <div className="text-md text-slate-900 mt-1">{c.studentCount} طالب</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-md text-slate-900">إجمالي المتبقي</div>
                  <div className="text-lg font-bold text-red-600">{Number(c.totalRemainder || 0).toLocaleString()}</div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

/* ------------------
   Details View
   ------------------ */
const DetailsView = ({ group, students, setStudents, newRows, setNewRows, search, setSearch }) => {
  // Derived totals
  const totalDebt = useMemo(() => {
    const existing = students.reduce((s, it) => s + Number(it.remainder || 0), 0);
    const pending = newRows.reduce((s, it) => s + Number(it.remainder || 0), 0);
    return existing + pending;
  }, [students, newRows]);

  // Filtered students
  const filtered = students.filter(s => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (s.fullName || "").toLowerCase().includes(q) || String(s.phone || "").includes(q);
  });

  // New row handlers
  const addNewRow = () => {
    setNewRows([...newRows, { ...emptyStudent, academicYear: group.academicYear, tempId: Date.now() }]);
  };
  const removeNewRow = (tempId) => setNewRows(newRows.filter(r => r.tempId !== tempId));
  const updateNewRow = (tempId, field, value) => {
    const updated = newRows.map(r => {
      if (r.tempId !== tempId) return r;
      const u = { ...r, [field]: value };
      if (field === "totalTuition" || field === "paidAmount") {
        u.remainder = Math.max(Number(u.totalTuition || 0) - Number(u.paidAmount || 0), 0);
      }
      return u;
    });
    setNewRows(updated);
  };
  const saveNewRow = async (tempId) => {
    const r = newRows.find(x => x.tempId === tempId);
    if (!r || !r.fullName) return alert("الاسم مطلوب");
    try {
      const payload = {
        academicYear: r.academicYear,
        fullName: r.fullName,
        totalTuition: Number(r.totalTuition || 0),
        paidAmount: Number(r.paidAmount || 0),
        remainder: Number(r.remainder || 0),
        phone: r.phone || null,
        note: r.note || null
      };
      const res = await createStaticFee(payload);
      setStudents(prev => [...prev, res.data]);
      removeNewRow(tempId);
    } catch (err) {
      console.error("saveNewRow error", err);
      alert(err?.response?.data?.error || "فشل الحفظ");
    }
  };

  // Existing student handlers
  const updateExistingLocal = (id, field, value) => {
    const updated = students.map(s => {
      if (s._id !== id) return s;
      const u = { ...s, [field]: value };
      if (field === "totalTuition" || field === "paidAmount") {
        u.remainder = Math.max(Number(u.totalTuition || 0) - Number(u.paidAmount || 0), 0);
      }
      return u;
    });
    setStudents(updated);
  };

  const handleDeleteExisting = async (id) => {
    if (!window.confirm("هل تريد حذف السجل؟")) return;
    try {
      await deleteStaticFee(id);
      setStudents(prev => prev.filter(s => s._id !== id));
    } catch (err) {
      console.error("deleteExisting error", err);
      alert("فشل الحذف");
    }
  };

  return (
    <div className="space-y-6">
      {/* Summary bar */}
      <div className="flex gap-4">
        <div className="bg-white p-4 rounded-xl shadow-md flex-1">
          <div className=" text-slate-900 text-lg font-semibold">عدد الطلاب : </div>
          <div className="text-xl font-bold mt-1">{students.length + newRows.length}</div>
        </div>
        <div className="bg-white p-4 rounded-xl shadow-md w-64">
          <div className=" text-slate-900 text-lg font-semibold">المجموع المتبقي : </div>
          <div className="text-2xl font-extrabold text-red-600 mt-1">{Number(totalDebt).toLocaleString()}</div>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white p-4 rounded-xl shadow-md">
        <input
          placeholder="ابحث بالاسم أو رقم الهاتف..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full border rounded px-3 py-2"
        />
      </div>

      {/* Table */}
      <div className="bg-white shadow-lg rounded-xl overflow-hidden border border-gray-200">
        <table className="w-full text-right">
          <thead className="bg-gray-50 text-slate-900 text-lg font-semibold">
            <tr>
              <th className="px-4 py-3">#</th>
              <th className="px-4 text-xl py-3 w-1/3">اسم الطالب</th>
              <th className="px-4 text-xl py-3">القسط </th>
              <th className="px-4 text-xl py-3">المدفوع</th>
              <th className="px-4 text-xl py-3 text-red-600">الباقي</th>
              <th className="px-4 text-xl py-3">الهاتف</th>
              <th className="px-4 text-xl py-3">ملاحظات</th>
              <th className="px-4 text-xl py-3 text-center">إجراءات</th>
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-100 text-md">
            {/* existing */}
            {filtered.map((s, idx) => (
              <EditableRow
                key={s._id}
                index={idx + 1}
                data={s}
                onLocalChange={updateExistingLocal}
                onSave={async (row) => {
                  try {
                    await updateStaticFee(row._id, {
                      fullName: row.fullName,
                      totalTuition: Number(row.totalTuition || 0),
                      paidAmount: Number(row.paidAmount || 0),
                      remainder: Number(row.remainder || 0),
                      phone: row.phone || null,
                      note: row.note || null
                    });
                    alert("تم الحفظ");
                  } catch (err) {
                    console.error("save existing error", err);
                    alert("فشل الحفظ");
                  }
                }}
                onDelete={() => handleDeleteExisting(s._id)}
              />
            ))}

            {/* new rows */}
            {newRows.map((r, i) => (
              <NewRow
                key={r.tempId}
                index={students.length + i + 1}
                data={r}
                onChange={(field, v) => updateNewRow(r.tempId, field, v)}
                onSave={() => saveNewRow(r.tempId)}
                onDelete={() => removeNewRow(r.tempId)}
              />
            ))}
          </tbody>
        </table>

        <div className="flex justify-between items-center p-3 bg-gray-50 border-t">
          <button onClick={addNewRow} className="bg-white border px-3 py-2 rounded hover:bg-gray-100">+ إضافة سطر جديد</button>
        </div>
      </div>
    </div>
  );
};

/* ------------------
   EditableRow component
   ------------------ */
const EditableRow = ({ index, data, onLocalChange, onSave, onDelete }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [local, setLocal] = useState({ ...data });

  React.useEffect(() => {
    setLocal({ ...data });
  }, [data]);

  const toggleEdit = async () => {
    if (isEditing) {
      // closing edit — save
      await onSave(local);
    }
    setIsEditing(!isEditing);
  };

  const handleChange = (field, value) => {
    const updated = { ...local, [field]: value };
    if (field === "totalTuition" || field === "paidAmount") {
      updated.remainder = Math.max(Number(updated.totalTuition || 0) - Number(updated.paidAmount || 0), 0);
    }
    setLocal(updated);
    onLocalChange(data._id, field, value);
  };

  return (
    <tr className="hover:bg-indigo-50/10 transition">
      <td className="px-4 py-3 text-lg text-slate-900">{index}</td>

      <td className="px-4 py-3">
        <input
          value={local.fullName}
          onChange={(e) => handleChange("fullName", e.target.value)}
          readOnly={!isEditing}
          className={`w-full ${isEditing ? 'border rounded px-2 py-1 text-lg font-medium' : 'bg-transparent text-lg font-medium'}`}
        />
      </td>

      <td className="px-4 py-3">
        <input
          type="number"
          value={local.totalTuition}
          onChange={(e) => handleChange("totalTuition", e.target.value)}
          readOnly={!isEditing}
          className={`w-28 ${isEditing ? 'border rounded px-2 py-1 text-md' : 'bg-transparent text-md'}`}
        />
      </td>

      <td className="px-4 py-3">
        <input
          type="number"
          value={local.paidAmount}
          onChange={(e) => handleChange("paidAmount", e.target.value)}
          readOnly={!isEditing}
          className={`w-28 ${isEditing ? 'border rounded px-2 py-1 text-md' : 'bg-transparent text-md'}`}
        />
      </td>

      <td className="px-4 py-3 font-extrabold text-red-600 text-lg bg-red-50/40">{Number(local.remainder || 0).toLocaleString()}</td>

      <td className="px-4 py-3">
        <input
          value={local.phone || ""}
          onChange={(e) => handleChange("phone", e.target.value)}
          readOnly={!isEditing}
          className={`${isEditing ? 'border rounded px-2 py-1 text-md' : 'bg-transparent text-md'}`}
        />
      </td>

      <td className="px-4 py-3">
        <input
          value={local.note || ""}
          onChange={(e) => handleChange("note", e.target.value)}
          readOnly={!isEditing}
          className={`${isEditing ? 'border rounded px-2 py-1 text-md' : 'bg-transparent text-md'}`}
        />
      </td>

      <td className="px-4 py-3 text-center flex gap-2 justify-center">
        <button
          onClick={toggleEdit}
          className={`px-3 py-1 rounded ${isEditing ? 'bg-green-700 text-white' : 'bg-yellow-300 text-yellow-800'}`}
        >
          {isEditing ? "حفظ" : "تعديل"}
        </button>

        <button onClick={() => onDelete(data._id)} className="px-3 py-1 rounded bg-red-500 text-white">حذف</button>
        
      </td>
    </tr>
  );
};

/* ------------------
   NewRow (pending row)
   ------------------ */
const NewRow = ({ index, data, onChange, onSave, onDelete }) => {
  return (
    <tr className="bg-yellow-50 border-l-4 border-yellow-300">
      <td className="px-4 py-3 font-bold text-yellow-700">{index}</td>

      <td className="px-4 py-3">
        <input value={data.fullName} onChange={(e) => onChange('fullName', e.target.value)} className="w-full border rounded px-2 py-1 font-medium" placeholder="اسم الطالب" />
      </td>

      <td className="px-4 py-3">
        <input type="number" value={data.totalTuition} onChange={(e) => onChange('totalTuition', e.target.value)} className="w-24 border rounded px-2 py-1" />
      </td>

      <td className="px-4 py-3">
        <input type="number" value={data.paidAmount} onChange={(e) => onChange('paidAmount', e.target.value)} className="w-24 border rounded px-2 py-1" />
      </td>

      <td className="px-4 py-3 font-bold text-red-600">{Number(data.remainder || 0).toLocaleString()}</td>

      <td className="px-4 py-3">
        <input value={data.phone || ""} onChange={(e) => onChange('phone', e.target.value)} className="w-36 border rounded px-2 py-1 text-sm" />
      </td>

      <td className="px-4 py-3">
        <input value={data.note || ""} onChange={(e) => onChange('note', e.target.value)} className="w-full border rounded px-2 py-1 text-sm" />
      </td>

      <td className="px-4 py-3 text-center flex gap-2 justify-center">
        <button onClick={onSave} className="px-3 py-1 rounded bg-green-600 text-white">حفظ</button>
        <button onClick={onDelete} className="px-3 py-1 rounded bg-red-200 text-red-700">إلغاء</button>
      </td>
    </tr>
  );
};

/* ------------------
   AddClassModal (uses fixed class options and years)
   ------------------ */
const AddClassModal = ({ years, onClose, onSuccess }) => {
  const [form, setForm] = useState({ academicYear: years[years.length - 1] });

  const handleSubmit = () => {
    if ( !form.academicYear) return alert("اختر السنة والصف");
    onSuccess({ academicYear: form.academicYear});
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-2xl p-6 w-96 shadow-2xl">
        <h3 className="text-lg font-bold mb-4">إنشاء ورقة عام دراسي</h3>
        <div className="space-y-3">
          <div>
            <label className="block text-sm mb-1">السنة الدراسية</label>
            <select className="w-full border rounded px-3 py-2" value={form.academicYear} onChange={(e) => setForm({ ...form, academicYear: e.target.value })}>
              {years.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>


          <div className="flex justify-between gap-3 pt-4">
            <button onClick={handleSubmit} className="flex-1 bg-indigo-600 text-white py-2 rounded">بدء التسجيل</button>
            <button onClick={onClose} className="flex-1 border py-2 rounded">إلغاء</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StaticFeesManager;
